import { useMemo, useState } from "react";
import { Button } from "../components/Button";
import { api } from "../lib/api";
import { useFetch } from "../lib/useFetch";
import { AcquireSheet } from "./catalog/AcquireSheet";
import type { CatalogItemDTO } from "@shared/api";

interface Props {
  search: string;
  onOpenSubscription: (subId: string) => void;
}

function badgeClass(badge: string): string {
  return `bbc-cat-badge bbc-cat-badge-${badge.toLowerCase()}`;
}

export function Catalog({ search, onOpenSubscription }: Props) {
  const [domain, setDomain] = useState("All");
  const [acquiring, setAcquiring] = useState<CatalogItemDTO | null>(null);

  const catalogQ = useFetch(() => api.catalog.list(), []);
  const servicesQ = useFetch(() => api.services.list(), []);

  const domains = useMemo(() => {
    const set = new Set<string>(["All"]);
    for (const c of catalogQ.data ?? []) set.add(c.domain);
    return [...set];
  }, [catalogQ.data]);

  const list = useMemo(() => {
    const items = catalogQ.data ?? [];
    return items.filter(
      (c) =>
        (domain === "All" || c.domain === domain) &&
        (!search ||
          c.name.toLowerCase().includes(search.toLowerCase()) ||
          c.domain.toLowerCase().includes(search.toLowerCase())),
    );
  }, [catalogQ.data, domain, search]);

  return (
    <div className="bbc-screen">
      <div className="bbc-screen-head">
        <div>
          <h1 className="bbc-h1">Catalog</h1>
          <p className="bbc-screen-sub">
            Acquire IP rights for additional use cases. Each catalog entry
            becomes a Subscription you configure with connected services,
            seats, and term length.
          </p>
        </div>
      </div>

      <div className="bbc-toolbar">
        <div className="bbc-segmented bbc-segmented-scroll">
          {domains.map((d) => (
            <button
              key={d}
              type="button"
              className={domain === d ? "is-on" : ""}
              onClick={() => setDomain(d)}
            >
              {d}
            </button>
          ))}
        </div>
        <div className="bbc-toolbar-right">
          <span className="bbc-result-count mono">{list.length} solutions</span>
        </div>
      </div>

      {catalogQ.loading ? (
        <div className="bbc-screen-loading">Loading catalog…</div>
      ) : catalogQ.error ? (
        <div className="bbc-error">{catalogQ.error.message}</div>
      ) : (
        <div className="bbc-catalog-grid">
          {list.map((c) => (
            <article key={c.id} className="bbc-cat-card">
              <div className="bbc-cat-art" data-domain={c.domain}>
                <span className="bbc-cat-art-tag mono">{c.domain}</span>
                {c.badge && (
                  <span className={badgeClass(c.badge)}>{c.badge}</span>
                )}
              </div>
              <div className="bbc-cat-body">
                <h4>{c.name}</h4>
                <p>{c.summary}</p>
                <div className="bbc-cat-meta">
                  <div>
                    <span className="dim">Stage</span>
                    <span className="mono">{c.stage}</span>
                  </div>
                  <div>
                    <span className="dim">From</span>
                    <span className="mono">{c.seatsFrom} seats</span>
                  </div>
                  <div>
                    <span className="dim">Min term</span>
                    <span className="mono">{c.termFrom} mo</span>
                  </div>
                </div>
                <div className="bbc-cat-actions">
                  <Button variant="ghost" size="sm">
                    View details
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => setAcquiring(c)}
                    disabled={!servicesQ.data}
                  >
                    Acquire →
                  </Button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {acquiring && servicesQ.data && (
        <AcquireSheet
          item={acquiring}
          services={servicesQ.data}
          onClose={() => setAcquiring(null)}
          onAcquired={async () => {
            // Refetch catalog so any future status changes show up.
            await catalogQ.refresh();
          }}
          onViewSubscription={(id) => {
            setAcquiring(null);
            onOpenSubscription(id);
          }}
        />
      )}
    </div>
  );
}
